import { Pressable, StyleSheet, View } from "react-native";
import { KeyboardAvoidingView } from "react-native-keyboard-controller";
import { ActivityIndicator, Button, TextInput } from "react-native-paper";
import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { setCompany } from "../../redux/slices/authSlice";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { RootStackParamList } from "../../types/navigationParams";
import {
  addDocumentWithoutCompany,
  update,
} from "../../services/firestoreServices";
import { OutlinedInput } from "../../components/fields/OutlinedInput";
import { useTranslation } from "react-i18next";
import { serverTimestamp } from "../../firebase";
import LogoutButton from "../../components/LogoutButton";
import { USER_ROLES } from "../../constants/constants";
import { USERS_COLLECTION } from "../../constants/collections";
import { getConfig } from "../../config/config";
import { extractRoleConfig } from "../../config/companies/roleConfig";

const companyTypes = [
  { label: "CTO", value: "STO" },
  { label: "Hotel", value: "hotel" },
  { label: "Delivery", value: "delivery" },
  { label: "Furniture", value: "furniture" },
];

type Props = NativeStackScreenProps<RootStackParamList, "CreateCompanyScreen">;

export const createCompany = async (
  userId: string,
  companyName: string,
  companyType: string,
) => {
  try {
    // Создание новой компании
    const companyData = {
      ownerId: userId,
      name: companyName,
      type: companyType,
      config: extractRoleConfig(getConfig(companyType)),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      deleted: false,
    };
    const newCompany = await addDocumentWithoutCompany(
      { name: "companies" },
      companyData,
    );

    // Обновление пользователя с новой компанией
    await update(USERS_COLLECTION, userId, {
      companyId: newCompany.id,
      company: {
        id: newCompany.id,
        name: companyName,
        ownerId: userId,
        type: companyType,
      },
      role: USER_ROLES.ADMIN,
    });

    return newCompany.id;
  } catch (error) {
    console.error("Error creating or setting company:", error);
    throw error;
  }
};

export default function CreateCompanyScreen({ navigation }: Props) {
  const [companyType, setCompanyType] = useState<string>("");
  const [companyName, setCompanyName] = useState("");
  const [loading, setLoading] = useState(true);

  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const user = useAppSelector((state) => state.auth.user);

  useEffect(() => {
    setLoading(false);
  }, []);

  const navToPickerScreen = () =>
    navigation.navigate("PickerScreen", {
      options: companyTypes.map((c) => c.value),
      onValueChange: (companyType) => setCompanyType(companyType),
      disableSearch: true,
    });

  const onSubmit = async () => {
    if (companyName.length < 1) {
      alert(t("Enter company name!"));
      return;
    } else if (!companyType) {
      alert(t("Select company type!"));
      return;
    }

    try {
      const newCompanyId = await createCompany(
        user.uid,
        companyName,
        companyType,
      );
      dispatch(
        setCompany({
          name: companyName,
          id: newCompanyId,
          type: companyType,
        }),
      );
      navigation.goBack();
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <View style={{ flex: 1, justifyContent: "center" }}>
        <ActivityIndicator size="large" />
      </View>
    );
  }

  return (
    <KeyboardAvoidingView style={styles.container} behavior="padding">
      <OutlinedInput
        testID="companyName"
        label={t("Company name")}
        value={companyName}
        onChangeText={setCompanyName}
      />
      <View>
        <OutlinedInput
          testID={"select-role"}
          value={t(companyType)}
          label={t("Company type")}
          editable={false}
          right={<TextInput.Icon icon="chevron-down" />}
        />
        <Pressable
          testID="pickerButton"
          onPress={navToPickerScreen}
          style={{
            position: "absolute",
            width: "100%",
            height: "100%",
          }}
        />
      </View>
      <Button
        testID={"create-company"}
        onPress={onSubmit}
        style={styles.submitButton}
      >
        {t("Save")}
      </Button>
      <LogoutButton style={{ marginTop: "auto" }} />
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    gap: 10,
  },
  title: {
    marginBottom: 20,
  },

  submitButton: {
    marginTop: 10,
    alignSelf: "flex-end",
  },
});
